import React from 'react';
import { motion } from 'framer-motion';
import { Package, CheckCircle, Shield, Clock } from 'lucide-react';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import spares1 from '../../../images/SpareParts/SpareService1.jpg';
import spares2 from '../../../images/SpareParts/SpareService2.jpeg';
import spares3 from '../../../images/SpareParts/SpareService3.jpeg';
import spares4 from '../../../images/SpareParts/SpareService4.jpeg';
import spares5 from '../../../images/SpareParts/SpareService5.jpeg';
import EnquiryForm from '../../../components/EnquiryForm/EnquiryForm';
import './SparesService.css';

const SparesService = () => {
  const features = [
    'SW gaskets and sealing solutions',
    'Graphite gland packing materials',
    'Pressure seal rings and components',
    'Gland ropes of various sizes',
    'Pan India availability',
    'Quality assurance and certification',
    'Reconditioning/refurbishment work'
  ];

  const sparesCategories = [
    {
      title: 'Gaskets & Seals',
      description: 'High-quality sealing solutions for all applications',
      icon: <Shield className="w-8 h-8" />,
      items: [
        'SW gaskets',
        'Spiral wound gaskets',
        'Ring joint gaskets',
        'Soft cut gaskets'
      ]
    },
    {
      title: 'Gland Packing',
      description: 'Reliable packing materials for valve stems',
      icon: <Package className="w-8 h-8" />,
      items: [
        'Graphite gland packing',
        'PTFE packing',
        'Carbon fiber packing',
        'Braided packing'
      ]
    },
    {
      title: 'Pressure Seals',
      description: 'Critical pressure seal components',
      icon: <Clock className="w-8 h-8" />,
      items: [
        'Pressure seal rings',
        'Seal retainers',
        'Backup rings',
        'O-rings'
      ]
    }
  ];

  const benefits = [
    'Genuine OEM parts',
    'Fast delivery across India',
    'Quality assurance',
    'Technical support',
    'Competitive pricing',
    'Warranty coverage'
  ];

  return (
    <div className="spares-service-page">
      {/* Hero Section */}
      <section className="spares-hero" style={{ backgroundImage: `url(${spares1})` }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="spares-hero-content"
          >
            <Breadcrumb />
            <h1 className="spares-hero-title">
              Genuine <span className="spares-hero-title-highlight">Spares Supply</span>
            </h1>
            <p className="spares-hero-description">
              Comprehensive spare parts support with genuine components across India. 
              Quality assurance and fast delivery for all your valve and actuator needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="spares-overview">
        <div className="container-custom">
          <div className="spares-overview-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="spares-overview-content"
            >
              <h2 className="spares-overview-title">Quality Spare Parts Solutions</h2>
              <p className="spares-overview-description">
                We offer soft spares support including SW gaskets, Graphite gland packing, 
                pressure seal rings, and gland ropes of various sizes. Our extensive inventory 
                ensures quick availability across India with quality assurance.
              </p>
              <div className="spares-features">
                {features.map((feature, index) => (
                  <div key={index} className="spares-feature-item">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="spares-overview-visual"
            >
              <div className="spares-image-gallery">
                {[
                  { src: spares1, alt: 'Quality Spare Parts', title: 'Quality Parts' },
                  { src: spares2, alt: 'Fast Delivery', title: 'Fast Delivery' },
                  { src: spares3, alt: 'Spare parts range', title: 'Wide Range' },
                  { src: spares4, alt: 'Genuine spares', title: 'Genuine Spares' },
                  { src: spares5, alt: 'Spare parts stock', title: 'Stock' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="spares-image-card"
                  >
                    <div className="spares-image-card-inner">
                      <img src={item.src} alt={item.alt} />
                      <div className="spares-image-card-overlay">
                        <h4>{item.title}</h4>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="spares-categories">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="spares-categories-header"
          >
            <h2 className="spares-categories-title">Spare Parts Categories</h2>
            <p className="spares-categories-description">
              Comprehensive range of quality spare parts
            </p>
          </motion.div>
          
          <div className="spares-categories-grid">
            {sparesCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="spares-category-card"
              >
                <div className="spares-category-icon">
                  <div className="spares-category-icon-wrapper">
                    {category.icon}
                  </div>
                </div>
                <h3 className="spares-category-title">{category.title}</h3>
                <p className="spares-category-description">{category.description}</p>
                <ul className="spares-category-items">
                  {category.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="spares-category-item">
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="spares-benefits">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="spares-benefits-header"
          >
            <h2 className="spares-benefits-title">Why Choose Our Spares?</h2>
            <p className="spares-benefits-description">
              Quality and reliability you can trust
            </p>
          </motion.div>
          
          <div className="spares-benefits-grid">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="spares-benefit-item"
              >
                <span className="spares-benefit-name">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="spares-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="spares-cta-content"
          >
            <h2 className="spares-cta-title">Need Quality Spare Parts?</h2>
            <p className="spares-cta-description">
              Get a free consultation and quote by filling out the form below
            </p>
            
            {/* Enquiry Form */}
            <div className="spares-cta-form">
              <EnquiryForm serviceName="Spare Parts" />
            </div>
            
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SparesService;
